import {
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import UserStatusChip from 'components/Map/UserStatusChip';
import { getTimePass } from 'shared/dates';
import { useProgressionModels } from 'features/wildfires/hooks/useProgressionModels';
import { ReactNode } from 'react';
import { useProgressionModelsSources } from 'features/wildfires/hooks/useProgressionModelsSources';

type ProgressionModelsListProps = {
  wildfireId: number;
  title?: ReactNode;
  excludeModel?: string;
};

export const ProgressionModelsList = (
  props: ProgressionModelsListProps,
): JSX.Element | null => {
  const { wildfireId, title, excludeModel } = props;
  const { t } = useTranslation();
  const { progressionModelsSources, isLoading: isLoadingSources } =
    useProgressionModelsSources();
  const { progressionModels, isLoading: isLoadingModels } =
    useProgressionModels({
      wildfireId,
    });

  const isLoading = isLoadingSources || isLoadingModels;

  return (
    <List
      subheader={
        <ListSubheader
          color="inherit"
          sx={(theme) => ({
            padding: theme.spacing(3, 2, 2),
            lineHeight: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
          })}
          disableSticky
        >
          {title ?? (
            <Stack direction="row" spacing={1}>
              <Typography variant="h3" sx={{ fontWeight: 'fontWeightBold' }}>
                {t('geoEvent.structuredInfo.progressionModels')}
              </Typography>
              <UserStatusChip type="pro" />
            </Stack>
          )}
        </ListSubheader>
      }
      disablePadding
    >
      {isLoading && (
        <ListItem divider role="status">
          <ListItemText
            primary={<Skeleton variant="text" width={80} />}
            secondary={<Skeleton variant="text" width="80%" />}
          />
        </ListItem>
      )}

      {progressionModelsSources.map((source) => {
        if (excludeModel && excludeModel === source.sourceAttribution) {
          return null;
        }
        const model = progressionModels.find(
          (m) => m.sourceAttribution === source.sourceAttribution,
        );
        return (
          <ListItemButton
            key={source.sourceAttribution}
            divider
            sx={{ '&.Mui-disabled': { opacity: 1 } }}
            component={Link}
            to={`/i/${wildfireId}/models/${model?.id}`}
            disabled={!model}
          >
            <ListItemText
              primary={
                <>
                  <Chip
                    label={
                      model
                        ? t('geoEvent.structuredInfo.available')
                        : t('geoEvent.structuredInfo.unavailable')
                    }
                    variant="watchduty"
                    sx={{ color: 'background.paper' }}
                    color={model ? 'success' : 'secondary'}
                  />
                  <Typography sx={{ fontWeight: 'fontWeightMedium' }}>
                    {source.sourceAttribution}
                  </Typography>
                </>
              }
              secondary={
                model
                  ? t('geoEvent.structuredInfo.updatedAgo', {
                      date: getTimePass(model.dateModified.toISOString()),
                    })
                  : undefined
              }
            />
            {Boolean(model) && (
              <ListItemIcon
                sx={(theme) => ({
                  minWidth: 'fit-content',
                  color: theme.palette.text.primary,
                })}
              >
                <KeyboardArrowRightIcon />
              </ListItemIcon>
            )}
          </ListItemButton>
        );
      })}
    </List>
  );
};
